/**
 * Model Configuration Component
 * Handles model URN input and authentication
 */

import React, { useState, useEffect, useRef } from "react";
import { useAccessToken } from "@/api/hooks/useAuth";
import { useViewerStore } from "@/store/viewerStore";
import { useRulesStore } from "@/store/rulesStore";
import { config } from "@/api/core/config";
import { MODEL_CONFIGS, getDefaultModelConfig } from "@/config/models";

interface ModelConfigurationProps {
  // Removed onModelLoaded - model loaded state is now managed via viewerStore
  onNext?: () => void; // Callback to navigate to next tab
}

export function ModelConfiguration({ onNext }: ModelConfigurationProps = {}) {
  
  // Custom user management
  const { customUsers, supportUser, addCustomUser, removeCustomUser, loadUsers } = useRulesStore();
  const [showAddUserForm, setShowAddUserForm] = useState(false);
  const [newUserName, setNewUserName] = useState("");
  const [newUserEmail, setNewUserEmail] = useState("");
  const [newUserRole, setNewUserRole] = useState<"engineer" | "architect" | "reviewer" | "admin" | "viewer">("engineer");
  
  // State selection
  const [selectedState, setSelectedState] = useState<string>("");
  const [stateInputValue, setStateInputValue] = useState<string>("");
  const [showStateDropdown, setShowStateDropdown] = useState(false);
  
  // Comments
  const [comments, setComments] = useState<string>("");
  
  // All 51 US states (50 states + DC)
  const usStates = [
    "Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado",
    "Connecticut", "Delaware", "Florida", "Georgia", "Hawaii", "Idaho",
    "Illinois", "Indiana", "Iowa", "Kansas", "Kentucky", "Louisiana",
    "Maine", "Maryland", "Massachusetts", "Michigan", "Minnesota",
    "Mississippi", "Missouri", "Montana", "Nebraska", "Nevada",
    "New Hampshire", "New Jersey", "New Mexico", "New York",
    "North Carolina", "North Dakota", "Ohio", "Oklahoma", "Oregon",
    "Pennsylvania", "Rhode Island", "South Carolina", "South Dakota",
    "Tennessee", "Texas", "Utah", "Vermont", "Virginia", "Washington",
    "West Virginia", "Wisconsin", "Wyoming", "District of Columbia"
  ];
  
  // Filter states based on input
  const filteredStates = usStates.filter(state =>
    state.toLowerCase().includes(stateInputValue.toLowerCase())
  );
  
  // Load users on mount
  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  // Viewer store
  const { modelUrn, loadModel, isModelLoaded, isLoading, viewer, selectedModelId, setSelectedModelId, selectedJurisdiction, setSelectedJurisdiction } =
    useViewerStore();

  // Pre-populate team members from model config when the model changes.
  // customUsers is not persisted, so this restores the configured defaults on every reload.
  // The user can remove any of them for the current session; they come back on next load.
  const hasPopulatedTeamRef = useRef<string | null>(null);
  useEffect(() => {
    if (hasPopulatedTeamRef.current === selectedModelId) return;
    // Clear existing custom users
    customUsers.forEach(u => removeCustomUser(u.id));
    // Load team members from model config if defined
    const modelConfig = MODEL_CONFIGS.find(m => m.id === selectedModelId);
    if (modelConfig?.teamMembers) {
      modelConfig.teamMembers.forEach(member => {
        addCustomUser({ name: member.name, email: member.email, role: member.role as any, isActive: true });
      });
    }
    hasPopulatedTeamRef.current = selectedModelId;
  }, [selectedModelId]);

  // React Query hooks
  const {
    data: tokenData,
    isLoading: isLoadingToken,
    error: tokenError,
  } = useAccessToken();

  const handleModelChange = (newModelId: string) => {
    setSelectedModelId(newModelId);
  };

  // Auto-load default model once when Model tab is shown and prerequisites are met
  const hasAutoLoadedRef = useRef(false);
  useEffect(() => {
    const defaultModel = getDefaultModelConfig();
    if (
      !hasAutoLoadedRef.current &&
      defaultModel.urn &&
      !modelUrn &&
      !isLoading &&
      tokenData?.token &&
      !tokenData.isExpired
    ) {
      hasAutoLoadedRef.current = true;
      loadModel(defaultModel.urn);
    }
  }, [modelUrn, isLoading, tokenData, loadModel]);

  return (
    <div className="animate-fade-in">
      <div className="space-y-4">
        <div>
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2 text-gray-800">
            ACC Revit Model
          </h2>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              BIM Model
            </label>
            <select
              value={selectedModelId}
              onChange={(e) => handleModelChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-custom-navy focus:border-custom-navy"
            >
              {MODEL_CONFIGS.filter(m => m.urn || m.id === 'skylight').map((model) => (
                <option key={model.id} value={model.id}>
                  {model.name}
                </option>
              ))}
            </select>
          </div>
          {isLoading && (
            <div className="text-xs text-gray-600 mt-1">Loading model...</div>
          )}
          {isModelLoaded && (
            <div className="text-xs text-green-600 mt-1">Model loaded</div>
          )}
        </div>

        {/* State Selection */}
        <div className="mt-6 pt-6 border-t border-gray-200">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                State
              </label>
              {selectedModelId === 'dutch-bros' ? (
                <div className="grid grid-cols-2 gap-3">
                  {([
                    { state: 'Texas', jurisdiction: 'Texas — ICC A117.1 · TAS 2012 · ADA 2010', standard: 'ICC A117.1 · TAS 2012 · ADA 2010' },
                    { state: 'Florida', jurisdiction: 'Florida — FBC 2023 + ADA 2010', standard: 'FBC 2023 + ADA 2010' },
                  ] as const).map((opt) => {
                    const isSelected = selectedJurisdiction === opt.jurisdiction ||
                      (!selectedJurisdiction && opt.state === 'Texas');
                    return (
                      <button
                        key={opt.state}
                        type="button"
                        onClick={() => setSelectedJurisdiction(opt.jurisdiction)}
                        className={`relative px-4 py-3 rounded-lg border-2 text-left transition-all duration-150 ${
                          isSelected
                            ? 'border-custom-navy bg-custom-navy/5 shadow-sm'
                            : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        <div className={`text-sm font-semibold ${isSelected ? 'text-custom-navy' : 'text-gray-800'}`}>
                          {opt.state}
                        </div>
                        <div className="text-xs text-gray-500 mt-0.5">{opt.standard}</div>
                        {isSelected && (
                          <div className="absolute top-2 right-2">
                            <svg className="w-4 h-4 text-custom-navy" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="relative">
                  <input
                    type="text"
                    value={stateInputValue}
                    onChange={(e) => {
                      setStateInputValue(e.target.value);
                      setShowStateDropdown(true);
                      if (usStates.includes(e.target.value)) {
                        setSelectedState(e.target.value);
                      } else {
                        setSelectedState("");
                      }
                    }}
                    onFocus={() => setShowStateDropdown(true)}
                    onBlur={() => {
                      setTimeout(() => setShowStateDropdown(false), 200);
                    }}
                    placeholder="Type to search state..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-custom-navy focus:border-custom-navy"
                  />
                  {showStateDropdown && filteredStates.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                      {filteredStates.map((state) => (
                        <button
                          key={state}
                          type="button"
                          onClick={() => {
                            setSelectedState(state);
                            setStateInputValue(state);
                            setShowStateDropdown(false);
                          }}
                          className="w-full px-3 py-2 text-left text-sm hover:bg-blue-50 focus:bg-blue-50 focus:outline-none transition-colors"
                        >
                          {state}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
            
            {/* Comments */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Comments
              </label>
              <textarea
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                placeholder="Which compliance documents are being used for this project?"
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-custom-navy focus:border-custom-navy resize-y"
              />
            </div>
          </div>
        </div>

        {/* Custom Users Section */}
        <div className="mt-6 pt-6 border-t border-gray-200">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-semibold text-gray-800">
              Team Members
            </h3>
            {customUsers.length < 3 && (
              <button
                onClick={() => setShowAddUserForm(!showAddUserForm)}
                className="px-3 py-1.5 text-sm font-medium text-custom-navy hover:text-custom-dark border border-custom-light-200 rounded-md hover:bg-custom-light-50 transition-colors"
              >
                + Add User ({customUsers.length}/3)
              </button>
            )}
          </div>

          {/* Add User Form */}
          {showAddUserForm && customUsers.length < 3 && (
            <div className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Name
                  </label>
                  <input
                    type="text"
                    value={newUserName}
                    onChange={(e) => setNewUserName(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-custom-navy focus:border-custom-navy"
                    placeholder="Enter name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    value={newUserEmail}
                    onChange={(e) => setNewUserEmail(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-custom-navy focus:border-custom-navy"
                    placeholder="Enter email"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Role
                  </label>
                  <select
                    value={newUserRole}
                    onChange={(e) => setNewUserRole(e.target.value as typeof newUserRole)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-custom-navy focus:border-custom-navy"
                  >
                    <option value="engineer">Engineer</option>
                    <option value="architect">Architect</option>
                    <option value="reviewer">Reviewer</option>
                    <option value="admin">Admin</option>
                    <option value="viewer">Viewer</option>
                  </select>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      if (newUserName.trim() && newUserEmail.trim()) {
                        addCustomUser({
                          name: newUserName.trim(),
                          email: newUserEmail.trim(),
                          role: newUserRole,
                          isActive: true,
                        });
                        setNewUserName("");
                        setNewUserEmail("");
                        setNewUserRole("engineer");
                        setShowAddUserForm(false);
                      }
                    }}
                    className="flex-1 px-4 py-2 text-sm font-medium text-white bg-custom-navy rounded-md hover:bg-custom-dark transition-colors"
                  >
                    Add
                  </button>
                  <button
                    onClick={() => {
                      setShowAddUserForm(false);
                      setNewUserName("");
                      setNewUserEmail("");
                      setNewUserRole("engineer");
                    }}
                    className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Custom Users List */}
          {customUsers.length > 0 && (
            <div className="space-y-2 mb-4">
              {customUsers.map((user) => (
                <div
                  key={user.id}
                  className="flex items-center justify-between p-3 bg-green-50 rounded-md border border-green-200"
                >
                  <div className="flex items-center gap-3 flex-1">
                    <div className="w-8 h-8 rounded-full bg-green-500 text-white flex items-center justify-center text-sm font-medium">
                      {user.name
                        .split(" ")
                        .map((n) => n[0])
                        .join("")
                        .toUpperCase()
                        .slice(0, 2)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-green-700 truncate">
                        {user.name}
                      </div>
                      <div className="text-xs text-gray-600 truncate">
                        {user.email}
                      </div>
                    </div>
                    <span className="text-xs px-2 py-1 bg-green-100 text-green-800 rounded-full font-medium">
                      {user.role === "vice_president"
                        ? "Vice President"
                        : user.role === "support_team"
                          ? "Support Team"
                          : user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                    </span>
                  </div>
                  <button
                    onClick={() => removeCustomUser(user.id)}
                    className="ml-3 text-red-600 hover:text-red-700 text-lg font-bold"
                    title="Remove user"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Support User - Always visible, non-removable */}
          <div className="mb-4">
            <div className="flex items-center justify-between p-3 bg-blue-50 rounded-md border border-blue-200">
              <div className="flex items-center gap-3 flex-1">
                <div className="w-8 h-8 rounded-full bg-blue-500 text-white flex items-center justify-center text-sm font-medium">
                  {supportUser.name
                    .split(" ")
                    .map((n) => n[0])
                    .join("")
                    .toUpperCase()
                    .slice(0, 2)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900 truncate">
                    {supportUser.name}
                  </div>
                  <div className="text-xs text-gray-600 truncate">
                    {supportUser.email}
                  </div>
                </div>
                <span className="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded-full font-medium">
                  Support
                </span>
              </div>
            </div>
          </div>

          {customUsers.length === 0 && !showAddUserForm && (
            <p className="text-sm text-gray-500 text-center py-4">
              No custom users added yet. Click "Add User" to add team members.
            </p>
          )}
        </div>

        {/* Next Button - Show when model is loaded */}
        {isModelLoaded && onNext && (
          <div className="mt-6">
            <button
              className="btn btn-primary w-full"
              onClick={onNext}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
